import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import EcoSpace from 'eco-design/base/eco-space';
import { Swiper, SwiperSlide } from 'swiper/react';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import { Navigation } from 'swiper';
import EcoImg from 'eco-design/base/eco-img';
import EcoSpaceSmall from 'eco-design/base/eco-space-small';
import EfIcon from '@elem/ui/ef-icon';
import EcoTitle from 'eco-design/base/eco-title';
import { useRouter } from 'next/router';
import { FormatMoneyLocal } from 'eco-design-micro/price/FormatMoneyLocal';
import { Swiper as SwiperType } from 'swiper/types';
import EcoButton from 'eco-design/base/eco-button';
import { useProductDispatch } from '../../../../ProductProvider';
import Dialog from './Dialog';
import styles from './index.module.scss';
import data from './data';
import { useLang } from '@/i18n';
import useProductInfo from '@/utils/hooks/useProductInfo';
import gtm from '@/utils/gtm';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';

const PowerSolution: React.FC = () => {
    const dispatch = useProductDispatch();

    // hooks
    const { t } = useLang('theProduct.PowerSolution');
    const { locale = 'jp' } = useRouter();

    const IS_JP = locale === 'jp';
    const IS_NA = ['ca', 'us'].includes(locale);
    const buy = useScheduledContent({
        '2025/06/24 15:00:00 GMT+8': t('presale'),
        '2025/07/15 15:00:00 GMT+8': t('buy'),
    });

    // state
    const [selectedProductIndex, setSelectedProductIndex] = useState(0);
    const [selectedColorIndices, setSelectedColorIndices] = useState([0, 0, 0, 0, 0]);
    const [swiperInstance, setSwiperInstance] = useState<SwiperType>();

    // 产品数据结构`
    const curData = useProductInfo((data[locale as 'jp'] as typeof data.jp) || data.jp, {
        variantImg: 'pc',
    } as const);
    useEffect(() => {
        // 其他模块也需要使用到产品信息，所以这里需要更新
        dispatch({
            type: 'changeProductInfo',
            payload: curData,
        });
    }, [curData, dispatch]);

    const productList = curData.map((i, idx) => ({
        ...i,
        children: i.children?.map?.((j: any) => ({
            ...j,
            lBtn: j?.lDisabled ? t('buy2') : undefined,
        })),
        title: t(`item${idx + 1}`),
        lBtn: IS_JP ? t('buy') : buy,
        lHref: `https://${locale}.ecoflow.com/products/${i?.handle}?variant=${i?.variantId}`,
        rBtn: t('amazon'),
        value1_name: t('name1'),
        value2_name: t('name2'),
        value1: t(`value${idx + 1}_1`),
        value2: t(`value${idx + 1}_2`),
    }));

    // 当前图片信息（用于展示）
    const currentProduct = useMemo(() => productList[selectedProductIndex], [productList, selectedProductIndex]);

    // 颜色切换
    const handleColorSelect = (e: MouseEvent<HTMLSpanElement>, productIdx: number, colorIdx: number) => {
        e.stopPropagation();
        const newColorSelections = [...selectedColorIndices];
        newColorSelections[productIdx] = colorIdx;
        setSelectedColorIndices(newColorSelections);
        const width = document.documentElement.clientWidth;
        if (width <= 1024) return;
        swiperInstance?.slideTo(colorIdx, 0, false);
    };

    // 监听当前产品变化,切换产品动画
    const animRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (animRef.current) {
            // 1. 先删除动画 class（重置）
            animRef.current.classList.remove('fade-in');
            // 2. 触发重绘，让浏览器认为 class 被真正移除了
            void animRef.current?.offsetWidth;
            // 3. 重新加上动画 class
            animRef.current?.classList.add('fade-in');
        }
    }, [selectedProductIndex]);

    // 埋点
    type GTM_Type = 'Left' | 'Right' | 'amazon';
    const handleGTM = (type: GTM_Type) => {
        let obj = {};
        switch (type) {
            case 'Right':
            case 'Left':
                obj = {
                    event: 'uaEvent',
                    event_name: 'product_detail_page',
                    list_name: t('title'),
                    button_name: `${type} switch`,
                };
                break;
            case 'amazon':
                obj = {};
                break;
        }
        gtm.push(obj);
    };

    const getProductsLink = (item: (typeof productList)[0], k: number) => {
        const product = item.children[selectedColorIndices[k]];
        return `https://${locale}.ecoflow.com/products/${product?.handle}?variant=${product?.variantId}`;
    };

    const [showNotifyMeModal, setShowNotifyMeModal] = useState(false);
    return (
        <>
            <div className={`${styles.comp} GtmModuleData-section`} data-section-id={'PowerSolution'} id="PowerSolution">
                <EcoSpace />
                <EcoTitle title={t('title')} />
                <EcoSpaceSmall />
                <div className="swiper-container">
                    <Swiper
                        className="box-swiper"
                        slidesPerView={'auto'}
                        spaceBetween={24}
                        speed={0}
                        navigation={{
                            nextEl: '.eco-swiper-button-next',
                            prevEl: '.eco-swiper-button-prev',
                        }}
                        key={selectedProductIndex}
                        modules={[Navigation]}
                        onTransitionStart={(swiper) => {
                            setSelectedColorIndices((state) => {
                                const newState = [...state];
                                newState[selectedProductIndex] = swiper.activeIndex;
                                return newState;
                            });
                        }}
                        onSwiper={(swiper) => {
                            setSwiperInstance(swiper);
                        }}
                        initialSlide={selectedColorIndices[selectedProductIndex]}
                    >
                        {productList[selectedProductIndex].children.map((i: any, index: number) => {
                            return (
                                <SwiperSlide key={index} className={`box-item`}>
                                    <div className="inner">
                                        <div className="img-box">
                                            <EcoImg {...i} />
                                        </div>
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                        {productList[selectedProductIndex].children.length > 1 && (
                            <div className="eco-swiper-control">
                                <div className="eco-swiper-navigation">
                                    <div className="eco-swiper-button-prev" onClick={() => handleGTM('Left')}>
                                        <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                    </div>
                                    <div className="eco-swiper-button-next" onClick={() => handleGTM('Right')}>
                                        <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Swiper>
                    <div className="swiper-info" ref={animRef}>
                        <div className="info-item info-item-1">
                            <div className="item-value">{currentProduct.value1}</div>
                            <div className="item-name">{currentProduct.value1_name}</div>
                        </div>
                        <div className="info-item info-item-2">
                            <div className="item-value">{currentProduct.value2}</div>
                            <div className="item-name">{currentProduct.value2_name}</div>
                        </div>
                    </div>
                </div>
                <div className="box-bottom">
                    {productList.map((item, k) => {
                        const data = item.children[selectedColorIndices[k]];
                        const discount = Math.round(((data.compareAtPrice - data.price) / data.compareAtPrice) * 100);
                        let isNotifyMe = (locale === 'us' && [0, 1, 3].includes(k)) || (locale === 'ca' && [0, 1].includes(k));
                        if (data.variantId === '54593884684361' || data.variantId === '52731646509427') {
                            isNotifyMe = false;
                        }
                        return (
                            <div
                                key={k}
                                className={`box-bottom-item GtmModuleData-btn-href item-${k + 1} box-bottom-item${
                                    selectedProductIndex === k ? '-active' : '-unselected'
                                }`}
                                onMouseEnter={() => {
                                    setSelectedProductIndex(k);
                                }}
                                onClick={() => {
                                    window.open(getProductsLink(item, k), '_blank');
                                    gtm.push({
                                        event: 'uaEvent',
                                        event_name: 'buy',
                                        product_name: item.title,
                                        position: `Collections_${t('title')}`,
                                        __btn_id1: `${styles.comp} .item-${k + 1}`,
                                        __btn_id2: getProductsLink(item, k),
                                        __btn_type: 'href',
                                    });
                                }}
                            >
                                <div className="box-bottom-item-img">
                                    <EcoImg pc={data?.pc} />
                                </div>
                                <ul className="colors">
                                    {item.children.length > 1 &&
                                        item.children?.map((color: any, m: number) => {
                                            return (
                                                <li className={`color color-${m + 1} ${selectedColorIndices[k] === m ? ' active' : ''}`} key={`${k}_${m}`}>
                                                    <span
                                                        className="outer GtmModuleData-btn-text"
                                                        style={{ borderColor: color.color }}
                                                        onClick={(e) => {
                                                            handleColorSelect(e, k, m);
                                                            gtm.push({
                                                                event: 'uaEvent',
                                                                event_name: 'product_detail_page',
                                                                list_name: t('title'),
                                                                button_name: `${item.title}_${color.color || color?.name}`,
                                                                __btn_id1: `${styles.comp} .item-${k + 1} .color-${m + 1}`,
                                                                __btn_id2: color.name,
                                                                __btn_type: 'text',
                                                            });
                                                        }}
                                                    >
                                                        <span className="inner" style={{ backgroundColor: color.color }}></span>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                </ul>
                                {!!data.price && !!data.compareAtPrice && (
                                    <div className="item-label">{discount ? `${discount}% ${t('off') ?? 'OFF'}` : ''}</div>
                                )}
                                <div className="title">{item.title}</div>
                                <div className="item-price">
                                    {item.children[selectedColorIndices[k]]?.price && (
                                        <div className="price">{(FormatMoneyLocal(data?.price, locale) || '').replace(/(\d)(€)/, '$1 $2')}</div>
                                    )}
                                    {data?.compareAtPrice && data?.compareAtPrice > data?.price && (
                                        <div className="discount">{(FormatMoneyLocal(data?.compareAtPrice, locale) || '').replace(/(\d)(€)/, '$1 $2')}</div>
                                    )}
                                </div>
                                <div className="item-link">
                                    {isNotifyMe ? (
                                        <div
                                            className="notify-me-btn notify GtmModuleData-btn-text"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowNotifyMeModal(true);
                                                gtm.push({
                                                    __btn_id1: `${styles.comp} .item-${k + 1} .notify`,
                                                    __btn_id2: t('notify-me'),
                                                    __btn_type: 'text',
                                                });
                                            }}
                                        >
                                            {t('notify-me')}
                                        </div>
                                    ) : (
                                        <EcoButton
                                            lHref={`https://${locale}.ecoflow.com/cart/${data.variantId}:1?channel=buy_button`}
                                            className="buy-btn buy GtmModuleData-btn-href"
                                            lDisabled={data?.lDisabled}
                                            lDataLayer={{
                                                event: 'uaEvent',
                                                event_name: 'checkout',
                                                button_name: `${data?.lBtn ?? item?.lBtn}_${item.title}`,
                                                __btn_id1: `${styles.comp} .item-${k + 1} .buy`,
                                                __btn_id2: `https://${locale}.ecoflow.com/cart/${data.variantId}:1?channel=buy_button`,
                                                __btn_type: 'href',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {data?.lBtn ?? item?.lBtn}
                                        </EcoButton>
                                    )}
                                    {data?.rHref && item?.rBtn && (
                                        <EcoButton
                                            lHref={data?.rHref}
                                            className="amazon-btn amazon"
                                            lDataLayer={{
                                                event: 'uaEvent',
                                                event_name: 'buy_on_amazon',
                                                product_name: item.title,
                                                button_name: item?.rBtn,
                                                list_name: t('title'),
                                                __btn_id1: `${styles.comp} .item-${k + 1} .amazon`,
                                                __btn_id2: data?.rHref,
                                                __btn_type: 'href',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <EfIcon pc="https://websiteoss.ecoflow.com/upload/image/1721387747560/a2.png" />
                                            {item?.rBtn}
                                        </EcoButton>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="footer-text">{t('footer-text')}</div>
            </div>
            <Dialog open={showNotifyMeModal} onClose={() => setShowNotifyMeModal(false)} />
        </>
    );
};
export default PowerSolution;
